import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import * as _ from 'lodash';
import moment from 'moment';
/**
 * Helper function for formatting the date in a human-readable format.
 *
 * **Note**: This function will format in UTC.
 *
 * @param {Date | moment.Moment | string} date - The date to format. Either as a string, Date object, or Moment object.
 * @param {boolean} truncateMonth - Whether or not to truncate the month (ie January -> Jan)
 * @returns The string formatted date (eg: "June 18, 2023").
 */
export const prettyMDY = (date: Date | moment.Moment | string, truncateMonth?: boolean): string => {
  let dateToFormat: Date;
  if (moment.isMoment(date)) {
    dateToFormat = date.toDate();
  } else if (_.isString(date)) {
    dateToFormat = new Date(date);
  } else {
    dateToFormat = parseISO(date.toISOString());
  }

  return formatInTimeZone(dateToFormat, 'Etc/Greenwich', truncateMonth ? 'LLL d, yyyy' : 'LLLL d, yyyy');
};

/**
 * Remaining lifetime until an ISO-8601 timestamp, in milliseconds (negative once
 * past, NaN when unparseable). Returns null for a null/empty input, so callers
 * can treat "no expiry known" separately from "expired".
 */
export function remainingLifetimeMs(expiresAt: string | null): number | null {
  if (!expiresAt) {
    return null;
  }
  return new Date(expiresAt).getTime() - Date.now();
}

/**
 * Returns a filename-safe timestamp in format: YYYY-MM-DD_HHMMSSsss
 * Useful for generating unique, sortable filenames.
 */
export function formatDateForFilename(date: Date = new Date()): string {
  const isoStringSplit = date.toISOString().split('T');
  // Since we know that these indices in the splits will be populated, 0 is just an arbitrary value for TS's sake.
  const timeStr = isoStringSplit[1]?.replace(/:/g, '').replace('.', '') || '0';
  return `${isoStringSplit[0]}_${timeStr}`;
}
